import { memo, useCallback, useState, useMemo, type CSSProperties } from 'react'
import { useReactFlow } from '@xyflow/react'
import NodeContainer from './shared/NodeContainer'
import { getNodeColorClasses, type NodeColor } from '@/lib/node-colors'
import { cn } from '@/lib/utils'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ChevronDown, ChevronRight, Maximize2, Ungroup, Trash2, Palette, Lock, Copy, Download } from 'lucide-react'

import type { AutoLayoutMode, BorderStyle } from '@/components/inspectors/SubFlowInspector'

export interface SubFlowNodeData {
  label?: string
  collapsed?: boolean
  childCount?: number
  /** Callbacks for context menu actions */
  onToggleCollapse?: () => void
  onExtractChildren?: () => void
  onDeleteWithChildren?: () => void
  onAutoResize?: () => void
  onCycleColor?: () => void
  onDuplicate?: () => void
  onSelectAllChildren?: () => void
  onExportAsImage?: () => void
  onApplyAutoLayout?: (mode: AutoLayoutMode) => void
  /** Drop target state for smooth visual feedback */
  isDropTarget?: boolean
  /** Resizing state for animation */
  isResizing?: boolean
  /** Flash effect when node is attached */
  flashAttached?: boolean
  /** Color theme for the group */
  color?: string
  /** Description/notes for the group */
  description?: string
  /** Whether the group is locked (prevents movement/editing) */
  locked?: boolean
  /** Background opacity (0-100) */
  opacity?: number
  /** Inner padding in pixels */
  padding?: number
  /** Auto-layout mode for children */
  autoLayout?: AutoLayoutMode
  /** Border style */
  borderStyle?: BorderStyle
}

interface SubFlowNodeProps {
  id: string
  data: SubFlowNodeData
  selected?: boolean
}

export default memo(function SubFlowNode({ id, data, selected }: SubFlowNodeProps) {
  const { getNode } = useReactFlow()
  const isCollapsed = data.collapsed || false
  const isDropTarget = data.isDropTarget || false
  const isResizing = data.isResizing || false
  const isLocked = data.locked || false
  const c = getNodeColorClasses(data?.color as NodeColor | undefined)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // Get opacity and border style with defaults
  const opacity = data.opacity ?? 10
  const borderStyle = data.borderStyle || 'solid'

  // Build dynamic class list for visual states
  const containerClasses = useMemo(() => {
    const classes: string[] = []

    // Base state classes
    if (selected) classes.push(c.ring)
    else classes.push(c.border)

    // Drop target highlighting (overflow.dev-style)
    if (isDropTarget) {
      classes.push('drop-target-active')
    }

    // Resizing animation state
    if (isResizing) {
      classes.push('is-resizing')
    }

    // Flash attached effect
    if (data?.flashAttached) {
      classes.push('flash-parent')
    }

    // Locked state
    if (isLocked) {
      classes.push('opacity-80 cursor-not-allowed')
    }

    return classes.filter(Boolean).join(' ')
  }, [selected, isDropTarget, isResizing, data?.flashAttached, isLocked, c.ring, c.border])

  // Calculate dynamic styles for color, opacity, and border
  const dynamicStyles = useMemo(() => ({
    '--group-color-rgb': c.rgb,
    '--group-bg-opacity': opacity / 100,
    '--group-border-color': `rgb(${c.rgb})`,
    '--group-border-style': borderStyle,
  } as CSSProperties), [opacity, c.rgb, borderStyle])

  const handleToggleCollapse = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onToggleCollapse?.()
    },
    [data]
  )

  const handleExtractChildren = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onExtractChildren?.()
    },
    [data]
  )

  const handleDeleteWithChildren = useCallback(() => {
    data.onDeleteWithChildren?.()
    setShowDeleteDialog(false)
  }, [data])

  const handleAutoResize = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onAutoResize?.()
    },
    [data]
  )

  const handleCycleColor = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onCycleColor?.()
    },
    [data]
  )

  const handleDeleteClick = useCallback((e: Event) => {
    e.stopPropagation()
    setShowDeleteDialog(true)
  }, [])

  const handleDuplicate = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onDuplicate?.()
    },
    [data]
  )

  const handleExportAsImage = useCallback(
    (e: Event) => {
      e.stopPropagation()
      data.onExportAsImage?.()
    },
    [data]
  )

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <NodeContainer
            variant="parent"
            kind="subflow"
            resizable={!isCollapsed && !isLocked}
            selected={selected}
            sourceFile="nodes/SubFlowNode.tsx"
            className={containerClasses}
            style={dynamicStyles}
          >
            {/* Floating title above the frame */}
            <div className="parent-title flex items-center gap-1.5">
              {isLocked && <Lock className="h-3 w-3 text-amber-600" />}
              {data?.label || 'Grupo'}
            </div>
            <div
              className="parent-inner"
              style={{
                backgroundColor: `rgba(${c.rgb}, ${opacity / 100})`
              }}
            />

            <div className="flex items-center justify-between gap-2">
              <div className="font-semibold text-xs" />
              {isCollapsed && data.childCount !== undefined && (
                <div className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                  {data.childCount} {data.childCount === 1 ? 'nodo' : 'nodos'}
                </div>
              )}
            </div>

            {isCollapsed && (
              <div className="mt-2 text-xs text-muted-foreground italic">
                {isLocked ? 'Grupo bloqueado y colapsado' : 'Grupo colapsado - click derecho para opciones'}
              </div>
            )}
          </NodeContainer>
        </ContextMenuTrigger>

        <ContextMenuContent className="w-56">
          <ContextMenuItem onSelect={handleToggleCollapse} disabled={isLocked}>
            {isCollapsed ? (
              <>
                <ChevronRight className="mr-2 h-4 w-4" />
                <span>Expandir</span>
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 h-4 w-4" />
                <span>Colapsar</span>
              </>
            )}
          </ContextMenuItem>

          {!isCollapsed && (
            <ContextMenuItem onSelect={handleAutoResize} disabled={isLocked}>
              <Maximize2 className="mr-2 h-4 w-4" />
              <span>Auto-ajustar tamaño</span>
            </ContextMenuItem>
          )}

          <ContextMenuItem onSelect={handleCycleColor} disabled={isLocked}>
            <Palette className="mr-2 h-4 w-4" />
            <span>Cambiar color</span>
          </ContextMenuItem>

          <ContextMenuSeparator />

          {/* Duplicate Group */}
          <ContextMenuItem onSelect={handleDuplicate}>
            <Copy className="mr-2 h-4 w-4" />
            <span>Duplicar grupo</span>
          </ContextMenuItem>

          {/* Export as Image */}
          <ContextMenuItem onSelect={handleExportAsImage}>
            <Download className="mr-2 h-4 w-4" />
            <span>Exportar como imagen</span>
          </ContextMenuItem>

          <ContextMenuSeparator />

          <ContextMenuItem onSelect={handleExtractChildren} disabled={isLocked}>
            <Ungroup className="mr-2 h-4 w-4" />
            <span>Desagrupar (mantener grupo)</span>
          </ContextMenuItem>

          <ContextMenuItem
            onSelect={handleDeleteClick}
            className="text-destructive focus:text-destructive"
            disabled={isLocked}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            <span>Eliminar grupo y contenido</span>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Confirmation dialog for destructive delete */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar grupo y todo su contenido?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción eliminará el grupo <strong>"{data?.label || 'Grupo'}"</strong> y{' '}
              <strong>TODOS los {data.childCount || 0} nodos dentro de él</strong>.
              <br />
              <br />
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWithChildren}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar todo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
})
